import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import DataExplorer from "./DataExplorer";
import DatasetView from "./DatasetView";
import DatasetForm from "./DatasetForm";
import StyledTabPanel, { a11yProps } from "../UI/StyledTabPanel";

import { defaultTabs } from "../../config/config.dataExplorer";
import { getData } from "../../store/dataSlice";
import { dataExplorerActions, dataEditActions } from "../../store";

/**
 * Renders the DataExplorerWrapper component.
 * This component displays either the DataExplorer or the DatasetView based on whether a dataset is selected.
 *
 * @param {boolean} showFilters - Determines whether to show filters by default in the DataExplorer component.
 * @param {boolean} useMobileLayout - Determines whether to use a mobile layout in the DatasetView component.
 * @param {string[]} tabsList - optional, a list of the entity IDs that should be available as tabs
 * @returns {JSX.Element} The rendered DataExplorerWrapper component.
 */
const DataExplorerWrapper = ({ showFilters, useMobileLayout, tabsList }) => {
  if (!tabsList) {
    tabsList = defaultTabs;
  }
  const dispatch = useDispatch();
  const selectedEntity = useSelector(
    (state) => state.dataExplorer.selectedEntity
  );

  // when component renders, always refetch data to reflect any updates
  useEffect(() => {
    dispatch(
      getData({ dataId: selectedEntity, source: "DataExplorerWrapper" })
    );
  }, [dispatch, selectedEntity]);

  // when component renders and selectedEntity is not among the tabs, set selectedEntity to the first tab
  useEffect(() => {
    if (!defaultTabs.includes(selectedEntity)) {
      dispatch(dataExplorerActions.setSelectedEntity(defaultTabs[0]));
    }
  }, [dispatch, selectedEntity]);

  // check if a dataset is selected to determine which view to show (all datasets or a single dataset)
  const selectedRow = useSelector((state) => state.dataExplorer.selectedRow);

  const isDatasetFormVisible = useSelector(
    (state) => state.dataEdit.isDatasetFormVisible
  );

  // determine initial tab on load
  const [value, setValue] = useState(1);

  // when selectedEntity changes, set tab to corresponding index
  useEffect(() => {
    setValue(
      defaultTabs.indexOf(selectedEntity) > -1
        ? defaultTabs.indexOf(selectedEntity)
        : 0
    );
  }, [selectedEntity]);

  // set value equal to selected tab index
  const handleChange = (_, newValue) => {
    setValue(newValue);
    dispatch(dataEditActions.showDatasetForm(false));
    dispatch(dataExplorerActions.setSelectedRow(null));
    dispatch(dataExplorerActions.setSelectedEntity(defaultTabs[newValue]));
  };

  // tab container styling
  const tabContainer = {
    borderColor: "divider",
    width: "100%",
    maxWidth: "lg",
    display: "flex",
    flexDirection: "column",
    // flexGrow: 1,
  };

  // tab styling
  const tabStyles = {
    backgroundColor: "#ffffff",
    "&.Mui-selected": {
      color: "#000000",
      backgroundColor: "#dbeaff",
    },
    "&:hover": {
      backgroundColor: "#dbeaff",
    },
  };

  return (
    <Box
      id="data-explorer-wrapper"
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        alignItems: "center",
      }}
    >
      {tabsList.length > 1 ? (
        <Box sx={tabContainer}>
          <Tabs value={value} onChange={handleChange} variant="fullWidth">
            {tabsList.map((tab, index) => (
              <Tab
                key={index}
                label={tab}
                {...a11yProps(index)}
                sx={tabStyles}
              />
            ))}
          </Tabs>
        </Box>
      ) : null}
      <Box
        sx={{
          flex: "auto",
          overflowY: "auto",
          alignItems: "center",
          width: "100%",
          maxWidth: "lg",
        }}
      >
        {tabsList.length > 1 ? (
          tabsList.map((tab, index) => (
            <StyledTabPanel key={index} value={value} index={index}>
              {selectedRow ? (
                isDatasetFormVisible ? (
                  <DatasetForm />
                ) : (
                  <DatasetView useMobileLayout={useMobileLayout} />
                )
              ) : (
                <DataExplorer showFilters={showFilters} dataName={tab} />
              )}
            </StyledTabPanel>
          ))
        ) : selectedRow ? (
          isDatasetFormVisible ? (
            <DatasetForm />
          ) : (
            <DatasetView useMobileLayout={useMobileLayout} />
          )
        ) : (
          <DataExplorer showFilters={showFilters} dataName={tabsList[0]} />
        )}
      </Box>
    </Box>
  );
};

export default DataExplorerWrapper;
